// src/components/ReferralIncomePage.jsx
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import PageWrapper from './PageWrapper'; // Assuming PageWrapper is in components
import axiosInstance from '../axiosInstance';
import { DollarSignIcon, LinkIcon, CopyIcon } from '../common/Icons'; // Icons for the page
import { LanguageContext } from '../contexts/LanguageContext'; // Import LanguageContext
import { ToastContainer, toast } from 'react-toastify'; // Import ToastContainer and toast

const ReferralIncomePage = ({ setCurrentPage, ...props }) => { // Destructure setCurrentPage
  const { language } = useContext(LanguageContext); // Use LanguageContext
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [referralHistory, setReferralHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(''); // Keep for critical page errors

  // Translations object
  const translations = {
    en: {
      referralIncome: "Referral Income",
      loadingReferralData: "Loading referral data...",
      failedToFetchProfile: "Failed to fetch user profile for referral.",
      failedToFetchHistory: "Failed to fetch referral history.",
      sessionExpired: "Your session has expired or is invalid. Redirecting to login...",
      networkError: "Network or server error.",
      error: "Error",
      goToLogin: "Go to Login",
      yourReferralCode: "Your Referral Code:",
      totalReferralEarnings: "Total Referral Earnings:",
      birr: "Birr",
      copyReferralCode: "Copy referral code",
      copied: "Copied!",
      copyFailed: "Failed to copy.",
      copyUnsupported: "Copy failed (unsupported by browser).",
      referralHistory: "Referral History",
      noReferralHistory: "No referral income history yet.",
      date: "Date",
      referredUser: "Referred User",
      earnings: "Earnings (ETB)",
      source: "Source",
      userId: "User ID:",
    },
    am: {
      referralIncome: "የሪፈራል ገቢ",
      loadingReferralData: "የሪፈራል ውሂብ በመጫን ላይ...",
      failedToFetchProfile: "የሪፈራል የተጠቃሚ መገለጫ ማግኘት አልተሳካም።",
      failedToFetchHistory: "የሪፈራል ታሪክ ማግኘት አልተሳካም።",
      sessionExpired: "የእርስዎ ክፍለ ጊዜ አልቋል ወይም ልክ ያልሆነ ነው። ወደ መግቢያ በመዞር ላይ...",
      networkError: "የአውታረ መረብ ወይም የአገልጋይ ስህተት።",
      error: "ስህተት",
      goToLogin: "ወደ መግቢያ ይሂዱ",
      yourReferralCode: "የእርስዎ ሪፈራል ኮድ:",
      totalReferralEarnings: "ጠቅላላ የሪፈራል ገቢዎች:",
      birr: "ብር",
      copyReferralCode: "የሪፈራል ኮድ ቅዳ",
      copied: "ተቀድቷል!",
      copyFailed: "መቅዳት አልተሳካም።",
      copyUnsupported: "መቅዳት አልተሳካም (በአሳሽ አይደገፍም)።",
      referralHistory: "የሪፈራል ታሪክ",
      noReferralHistory: "እስካሁን ምንም የሪፈራል ገቢ ታሪክ የለም።",
      date: "ቀን",
      referredUser: "የተጠቀሰ ተጠቃሚ",
      earnings: "ገቢዎች (ETB)",
      source: "ምንጭ",
      userId: "የተጠቃሚ መታወቂያ:",
    },
    or: {
      referralIncome: "Galii Ergaa",
      loadingReferralData: "Odeeffannoo ergaa fe'amaa jira...",
      failedToFetchProfile: "Profaayilii fayyadamaa ergaadhaaf argachuu hindandeenye.",
      failedToFetchHistory: "Seenaa ergaa argachuu hindandeenye.",
      sessionExpired: "Sesshiniin keessan xumureera ykn hin hojjetu. Gara seensaatti deebi'aa...",
      networkError: "Dogoggora networkii ykn serverii.",
      error: "Dogoggora",
      goToLogin: "Gara seensaatti deebi'i",
      yourReferralCode: "Koodii Ergaa keessan:",
      totalReferralEarnings: "Injifannoo Ergaa Waliigalaa:",
      birr: "Birr",
      copyReferralCode: "Koodii Ergaa Maxxansi",
      copied: "Copia gochuu!",
      copyFailed: "Copia gochuu hin milkoofne.",
      copyUnsupported: "Copia gochuu hin milkoofne (browseriin hin deeggaru).",
      referralHistory: "Seenaa Ergaa",
      noReferralHistory: "Ammas seenaan galii ergaa hin jiru.",
      date: "Guyyaa",
      referredUser: "Fayyadamaa ergamedha",
      earnings: "Galii (ETB)",
      source: "Madda",
      userId: "ID Fayyadamaa:",
    },
    ti: {
      referralIncome: "ገቢ ሪፈራል",
      loadingReferralData: "ናይ ሪፈራል መረጃ ይጽዕን ኣሎ...",
      failedToFetchProfile: "ናይ ሪፈራል ተጠቃሚ ፕሮፋይል ምምጻእ ኣይከኣለን።",
      failedToFetchHistory: "ናይ ሪፈራል ታሪኽ ምምጻእ ኣይከኣለን።",
      sessionExpired: "ናይ'ዚ ስራሕ ግዜኹም ወዲኡ ወይ ድማ ቅኑዕ ኣይኮነን። ናብ ምእታው ይምለስ ኣሎ...",
      networkError: "ጌጋ መርበብ ወይ ሰርቨር።",
      error: "ጌጋ",
      goToLogin: "ናብ ምእታው ይኸይድ",
      yourReferralCode: "ናይ ሪፈራል ኮድኩም:",
      totalReferralEarnings: "ጠቕላላ ገቢ ሪፈራል:",
      birr: "ብር",
      copyReferralCode: "ናይ ሪፈራል ኮድ ቅዳሕ",
      copied: "ተቐዲሑ!",
      copyFailed: "ምቕዳሕ ኣይተዓወተን።",
      copyUnsupported: "ምቕዳሕ ኣይተዓወተን (ብብሮውዘር ኣይድገፍን)።",
      referralHistory: "ታሪኽ ሪፈራል",
      noReferralHistory: "እስካሁን ምንም የሪፈራል ገቢ ታሪክ የለም።",
      date: "ዕለት",
      referredUser: "ዝተጠቐሰ ተጠቃሚ",
      earnings: "ገቢታት (ETB)",
      source: "ምንጪ",
      userId: "ID ተጠቃሚ:",
    },
    wo: {
      referralIncome: "Galii Ergaa",
      loadingReferralData: "Odeeffannoo ergaa fe'amaa jira...",
      failedToFetchProfile: "Profaayilii fayyadamaa ergaadhaaf argachuu hindandeenye.",
      failedToFetchHistory: "Seenaa ergaa argachuu hindandeenye.",
      sessionExpired: "Sesshiniin keessan xumureera ykn hin hojjetu. Gara seensaatti deebi'aa...",
      networkError: "Dogoggora networkii ykn serverii.",
      error: "Dogoggora",
      goToLogin: "Gara seensaatti deebi'i",
      yourReferralCode: "Koodii Ergaa keessan:",
      totalReferralEarnings: "Injifannoo Ergaa Waliigalaa:",
      birr: "Birr",
      copyReferralCode: "Koodii Ergaa Maxxansi",
      copied: "Copia gochuu!",
      copyFailed: "Copia gochuu hin milkoofne.",
      copyUnsupported: "Copia gochuu hin milkoofne (browseriin hin deeggaru).",
      referralHistory: "Seenaa Ergaa",
      noReferralHistory: "Ammas seenaan galii ergaa hin jiru.",
      date: "Guyyaa",
      referredUser: "Fayyadamaa ergamedha",
      earnings: "Galii (ETB)",
      source: "Madda",
      userId: "ID Fayyadamaa:",
    },
  };

  // Translation helper function
  const t = useCallback((key, variables = {}) => {
    let translation = translations[language]?.[key] || translations.en[key] || key;
    for (const [placeholder, value] of Object.entries(variables)) {
      translation = translation.replace(`{${placeholder}}`, value);
    }
    return translation;
  }, [language]);

  // Toast utility function
  const showToast = useCallback((message, type = 'error') => {
    toast[type](message, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }, []);

  const fetchReferralData = useCallback(async () => {
    try {
      setLoading(true);
      const userProfileResponse = await axiosInstance.get('/user/profile');
      if (userProfileResponse.data.success) {
        setUserData(userProfileResponse.data.user);
      } else {
        showToast(userProfileResponse.data.message || t('failedToFetchProfile'), 'error');
        setError(t('error')); // Still set a generic error for the page
        setLoading(false);
        return;
      }

      const referralHistoryResponse = await axiosInstance.get('/user/referral-history');
      if (referralHistoryResponse.data.success) {
        setReferralHistory(referralHistoryResponse.data.referrals);
      } else {
        showToast(referralHistoryResponse.data.message || t('failedToFetchHistory'), 'error');
        setError(t('error')); // Still set a generic error for the page
      }
    } catch (err) {
      console.error('Error fetching referral data:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        showToast(t('sessionExpired'), 'error');
        setError(t('sessionExpired'));
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        showToast(err.response?.data?.message || t('networkError'), 'error');
        setError(t('networkError'));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, t, showToast]);

  useEffect(() => {
    setCurrentPage(t('referralIncome')); // Use destructured setCurrentPage
    fetchReferralData();
  }, [setCurrentPage, fetchReferralData, t]); // Removed 'props' from dependencies

  const handleCopyReferralCode = () => {
    if (userData?.referralCode) {
      // Use document.execCommand('copy') for better compatibility in iframes
      const textArea = document.createElement('textarea');
      textArea.value = userData.referralCode;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          showToast(t('copied'), 'info'); // Use toast for copied message
        } else {
          showToast(t('copyFailed'), 'error'); // Use toast for copy failed
        }
      } catch (err) {
        console.error('Failed to copy text:', err);
        showToast(t('copyUnsupported'), 'error'); // Use toast for unsupported
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <PageWrapper {...props}>
        <div className="flex justify-center items-center h-screen">
          <p className="text-gray-700 text-lg">{t('loadingReferralData')}</p>
        </div>
      </PageWrapper>
    );
  }

  // Only show the error page if a critical error occurred that prevents display
  if (error && (error === t('sessionExpired') || error === t('networkError'))) {
    return (
      <PageWrapper {...props}>
        <div className="flex flex-col justify-center items-center h-screen text-red-600">
          <p className="text-lg mb-2">{t('error')}: {error}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md"
          >
            {t('goToLogin')}
          </button>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper {...props} backgroundClass="bg-gradient-to-br from-teal-800 to-blue-900 text-white min-h-screen">
      <ToastContainer /> {/* Add ToastContainer here */}
      <div className="relative max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-center mb-8 flex items-center justify-center space-x-3">
          <DollarSignIcon className="w-8 h-8 text-yellow-400" />
          <span>{t('referralIncome')}</span>
        </h1>

        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 mb-8 border border-white border-opacity-20 flex flex-col sm:flex-row items-center justify-between">
          <div className="text-center sm:text-left mb-4 sm:mb-0">
            <p className="text-sm text-gray-300">{t('yourReferralCode')}</p>
            <p className="text-2xl font-extrabold text-white flex items-center justify-center sm:justify-start">
              {userData?.referralCode || 'N/A'}
              {userData?.referralCode && (
                <button
                  onClick={handleCopyReferralCode}
                  className="ml-3 p-2 rounded-full bg-blue-600 hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
                  title={t('copyReferralCode')}
                >
                  <CopyIcon className="w-5 h-5 text-white" />
                </button>
              )}
            </p>
          </div>
          <div className="text-center sm:text-right">
            <p className="text-sm text-gray-300">{t('totalReferralEarnings')}</p>
            <p className="text-3xl font-extrabold text-green-400">
              {userData?.totalReferralEarnings !== undefined ? parseFloat(userData.totalReferralEarnings).toFixed(2) : '0.00'} {t('birr')}
            </p>
          </div>
        </div>

        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-white border-opacity-20 overflow-hidden">
          <h2 className="text-2xl font-bold mb-4 flex items-center space-x-2">
            <LinkIcon className="w-6 h-6 text-yellow-400" />
            <span>{t('referralHistory')}</span>
          </h2>
          {referralHistory.length === 0 ? (
            <p className="text-gray-300 text-center">{t('noReferralHistory')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-700 bg-opacity-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('date')}</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('referredUser')}</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('earnings')}</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('source')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {referralHistory.map((referral) => (
                    <tr key={referral.id} className="hover:bg-gray-700 hover:bg-opacity-20">
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-200">
                        {new Date(referral.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-200">
                        {referral.referred_user_full_name || `${t('userId')} ${referral.referred_user_id}`}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-green-300">
                        {parseFloat(referral.amount).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-200">
                        {referral.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PageWrapper>
  );
};

export default ReferralIncomePage;
